/**
 * RegisterShipmentDrawer — entry point for externally-booked loads.
 *
 * For freight that was booked on a carrier portal (not via the ops quote/tender
 * flow). Calls POST /api/freight/register-external which creates a
 * freight_shipments row, writes freight_ref / freight_carrier_name / freight_bol
 * to the Odoo PO, and attempts a live tracking poll for registered providers.
 *
 * Used from the SPI Freight board header. Compare with ConfirmCarrierBookingModal
 * in material-pickup (same API, but attached to a pickup record).
 */

import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import { useRegisterExternalShipment, type RegisterExternalShipmentBody } from '../api/queries'

const KNOWN_PROVIDERS = [
  { key: 'tql', label: 'TQL Logistics' },
  { key: 'echo_gl', label: 'Echo Global Logistics' },
  { key: 'wwex', label: 'WWEX / Unishippers' },
  { key: 'manual', label: 'Other carrier (UPS, FedEx, LTL…)' },
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRegistered?: (shipmentId: number) => void
}

/** Which number to enter per provider. Prod 2026-10-21: most failing rows
 * held the vendor's order number or an Echo SAP number, not the ID the
 * carrier tracks by. */
const REF_HINTS: Record<string, string> = {
  tql: 'TQL PO# from the tender confirmation (8 digits, e.g. 36844121). Not the vendor order number.',
  echo_gl: 'Echo shipment ID (8 digits, e.g. 68822003). Not the Echo SAP or BOL number.',
  manual:
    "The carrier's tracking or PRO number, with the carrier's name above. UPS, FedEx and most LTL carriers are then tracked automatically. Not the vendor's order number.",
}

export function RegisterShipmentDrawer({ open, onOpenChange, onRegistered }: Props) {
  const register = useRegisterExternalShipment()
  const [poName, setPoName] = useState('')
  const [carrierName, setCarrierName] = useState('')
  const [provider, setProvider] = useState('manual')
  const [providerRef, setProviderRef] = useState('')
  const [bolNumber, setBolNumber] = useState('')
  const [pickupDate, setPickupDate] = useState('')
  const [estDelivery, setEstDelivery] = useState('')
  const [notes, setNotes] = useState('')

  if (!open) return null

  function resetForm() {
    setPoName(''); setCarrierName(''); setProvider('manual')
    setProviderRef(''); setBolNumber(''); setPickupDate('')
    setEstDelivery(''); setNotes('')
  }

  function handleClose() {
    resetForm()
    onOpenChange(false)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!poName.trim() || !carrierName.trim() || !providerRef.trim()) return

    const body: RegisterExternalShipmentBody = {
      odoo_po_name: poName.trim(),
      carrier_name: carrierName.trim(),
      provider,
      provider_ref: providerRef.trim(),
    }
    if (bolNumber.trim()) body.bol_number = bolNumber.trim()
    if (pickupDate) body.pickup_date = pickupDate
    if (estDelivery) body.est_delivery = estDelivery
    if (notes.trim()) body.notes = notes.trim()

    register.mutate(body, {
      onSuccess: (result) => {
        toast.success(
          result.live_tracking
            ? 'Shipment registered with live tracking'
            : 'Shipment registered — carrier ref written to Odoo PO',
        )
        handleClose()
        onRegistered?.(result.shipment_id)
      },
      onError: (err) => {
        // 409: this PO already has a shipment under this provider (detail
        // names the existing ref/status). 422: the reference is a
        // placeholder. Surface the plain detail text, not "path 409: …".
        const msg =
          err instanceof ApiError && (err.status === 409 || err.status === 422) && typeof err.detail === 'string'
            ? err.detail
            : err instanceof Error
              ? err.message
              : 'registration failed'
        toast.error(msg, { duration: 8000 })
      },
    })
  }

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-40 flex items-center justify-end bg-black/30">
      {/* Drawer panel */}
      <div className="flex h-full w-full max-w-md flex-col border-l border-border-subtle bg-surface shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <div>
            <h2 className="text-role-page-title m-0">Register external shipment</h2>
            <p className="text-role-page-subtitle mt-0.5 m-0">
              For loads booked on a carrier portal, not through the ops quote flow.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            ✕
          </Button>
        </div>

        <form id="register-shipment-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-po">
              Odoo PO <span className="text-danger-text">*</span>
            </label>
            <input
              id="rs-po"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={poName}
              onChange={(e) => setPoName(e.target.value)}
              placeholder="e.g. P01756"
              required
            />
          </div>

          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-carrier">
              Carrier name <span className="text-danger-text">*</span>
            </label>
            <input
              id="rs-carrier"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              placeholder="e.g. Estes Express"
              required
            />
          </div>

          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-provider">
              Provider (SPI registry)
            </label>
            <select
              id="rs-provider"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {KNOWN_PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-ref">
              Tracking ref / PRO / carrier # <span className="text-danger-text">*</span>
            </label>
            <input
              id="rs-ref"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={providerRef}
              onChange={(e) => setProviderRef(e.target.value)}
              placeholder="BOL, PRO, or tracking reference"
              required
            />
            {REF_HINTS[provider] ? (
              <p className="text-tertiary text-xs mt-1">{REF_HINTS[provider]}</p>
            ) : null}
          </div>

          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-bol">
              BOL number <span className="text-tertiary">(if different)</span>
            </label>
            <input
              id="rs-bol"
              type="text"
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
              value={bolNumber}
              onChange={(e) => setBolNumber(e.target.value)}
              placeholder="Optional"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-role-metadata block mb-1" htmlFor="rs-pickup">Pickup date</label>
              <input
                id="rs-pickup"
                type="date"
                className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
              />
            </div>
            <div>
              <label className="text-role-metadata block mb-1" htmlFor="rs-delivery">Est. delivery</label>
              <input
                id="rs-delivery"
                type="date"
                className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none"
                value={estDelivery}
                onChange={(e) => setEstDelivery(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-role-metadata block mb-1" htmlFor="rs-notes">Notes</label>
            <textarea
              id="rs-notes"
              rows={3}
              className="w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-primary focus:border-brand focus:outline-none resize-none"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional"
            />
          </div>
        </form>

        <div className="border-t border-border-subtle px-6 py-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={handleClose} disabled={register.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="register-shipment-form"
            variant="primary"
            size="sm"
            disabled={
              register.isPending ||
              !poName.trim() ||
              !carrierName.trim() ||
              !providerRef.trim()
            }
          >
            {register.isPending ? 'Registering…' : 'Register shipment'}
          </Button>
        </div>
      </div>
    </div>
  )
}
